import { useState, useEffect } from 'react'
import axios from 'axios'
import { Monitor, Smartphone, Tablet, AlertTriangle, Check, Trash2, LogOut } from '@werkstatt/ui'

import { logger } from '@/utils/logger'

interface Session {
  id: number
  browser: string
  os: string
  device_type: string
  ip_address: string
  created_at: string
  last_activity: string
  is_current: boolean
}

function getCsrfToken() {
  return document.cookie
    .split('; ')
    .find((row) => row.startsWith('csrf_token='))
    ?.split('=')[1]
}

function getDeviceIcon(deviceType: string) {
  switch (deviceType) {
    case 'mobile':
      return <Smartphone size={18} />
    case 'tablet':
      return <Tablet size={18} />
    default:
      return <Monitor size={18} />
  }
}

function formatRelative(dateString: string) {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMins < 1) return 'Gerade eben'
  if (diffMins < 60) return `Vor ${diffMins} Minute${diffMins !== 1 ? 'n' : ''}`
  if (diffHours < 24) return `Vor ${diffHours} Stunde${diffHours !== 1 ? 'n' : ''}`
  if (diffDays < 7) return `Vor ${diffDays} Tag${diffDays !== 1 ? 'en' : ''}`

  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

export default function CustomerSessionsPanel() {
  const [sessions, setSessions] = useState<Session[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [revokingId, setRevokingId] = useState<number | null>(null)
  const [revokingAll, setRevokingAll] = useState(false)
  const [legacyNotice, setLegacyNotice] = useState(false)

  useEffect(() => {
    loadSessions()
  }, [])

  const loadSessions = async () => {
    try {
      setLoading(true)
      setError('')
      const response = await axios.get('/api/v1/auth/sessions', { withCredentials: true })
      setSessions(response.data.sessions || [])

      if (response.data.legacy_token_upgraded) {
        setLegacyNotice(true)
        setTimeout(() => setLegacyNotice(false), 10000)
      }
    } catch (err) {
      setError('Sitzungen konnten nicht geladen werden')
      logger.error('Error loading sessions:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleRevokeSession = async (sessionId: number) => {
    if (!confirm('Möchten Sie diese Session wirklich beenden?')) return

    try {
      setRevokingId(sessionId)
      setError('')
      await axios.delete(`/api/v1/auth/sessions/${sessionId}`, {
        withCredentials: true,
        headers: { 'X-CSRF-Token': getCsrfToken() },
      })
      await loadSessions()
    } catch (err) {
      setError('Sitzung konnte nicht beendet werden')
      logger.error('Error revoking session:', err)
    } finally {
      setRevokingId(null)
    }
  }

  const handleRevokeAllSessions = async () => {
    if (!confirm('Möchten Sie wirklich alle anderen Sessions beenden? Sie bleiben nur auf diesem Gerät angemeldet.')) return

    try {
      setRevokingAll(true)
      setError('')
      await axios.post('/api/v1/auth/sessions/logout-all', {}, {
        withCredentials: true,
        headers: { 'X-CSRF-Token': getCsrfToken() },
      })
      await loadSessions()
    } catch (err) {
      setError('Sitzungen konnten nicht beendet werden')
      logger.error('Error revoking all sessions:', err)
    } finally {
      setRevokingAll(false)
    }
  }

  if (loading) {
    return (
      <div className="wc-card wc-elev-sm" style={{ alignItems: 'center', padding: '40px 0' }}>
        <div className="wc-spinner" />
      </div>
    )
  }

  const currentSession = sessions.find((s) => s.is_current)
  const otherSessions = sessions.filter((s) => !s.is_current)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="wc-card wc-elev-sm" style={{ gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <h3 className="wc-card-title" style={{ margin: 0 }}>Aktive Sitzungen</h3>
            <p className="wc-muted" style={{ fontSize: 13, margin: '4px 0 0' }}>
              Verwalten Sie Ihre aktiven Anmeldesitzungen auf verschiedenen Geräten
            </p>
          </div>
          {otherSessions.length > 0 && (
            <button
              onClick={handleRevokeAllSessions}
              disabled={revokingAll}
              className="wc-btn wc-btn-secondary"
              style={{ fontSize: 13, color: '#fca5a5', borderColor: 'color-mix(in srgb, #fca5a5 45%, transparent)' }}
            >
              <LogOut size={14} />
              {revokingAll ? 'Wird bearbeitet…' : 'Alle anderen beenden'}
            </button>
          )}
        </div>

        {error && <div className="wc-alert wc-alert-error">{error}</div>}

        {legacyNotice && (
          <div className="wc-alert wc-alert-success">
            <strong>✓ Session erstellt:</strong> Ihre aktuelle Session wurde automatisch registriert.
          </div>
        )}

        {sessions.length === 0 ? (
          <p className="wc-muted" style={{ fontSize: 13, textAlign: 'center', padding: '16px 0' }}>Keine aktiven Sessions gefunden.</p>
        ) : (
          <>
            {currentSession && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 14,
                  padding: 14,
                  borderRadius: 'var(--wc-radius-md)',
                  border: '1px solid color-mix(in srgb, var(--wc-accent) 40%, transparent)',
                  background: 'color-mix(in srgb, var(--wc-accent) 10%, transparent)',
                }}
              >
                <div style={{ marginTop: 2, color: 'var(--wc-accent-300)' }}>{getDeviceIcon(currentSession.device_type)}</div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 14 }}>{currentSession.browser} auf {currentSession.os}</span>
                    <span className="wc-tag wc-tag-accent" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Check size={11} /> Aktuelle Sitzung
                    </span>
                  </div>
                  <div className="wc-muted" style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span>IP-Adresse: {currentSession.ip_address}</span>
                    <span>Angemeldet: {formatRelative(currentSession.created_at)}</span>
                    <span>Letzte Aktivität: {formatRelative(currentSession.last_activity)}</span>
                  </div>
                </div>
              </div>
            )}

            {otherSessions.map((session) => (
              <div
                key={session.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 14,
                  padding: 14,
                  borderRadius: 'var(--wc-radius-md)',
                  border: '1px solid var(--wc-divider)',
                }}
              >
                <div style={{ marginTop: 2 }} className="wc-muted">{getDeviceIcon(session.device_type)}</div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <p style={{ fontSize: 14, margin: '0 0 4px' }}>{session.browser} auf {session.os}</p>
                  <div className="wc-muted" style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span>IP-Adresse: {session.ip_address}</span>
                    <span>Angemeldet: {formatRelative(session.created_at)}</span>
                    <span>Letzte Aktivität: {formatRelative(session.last_activity)}</span>
                  </div>
                </div>
                <button
                  onClick={() => handleRevokeSession(session.id)}
                  disabled={revokingId === session.id}
                  className="wc-btn wc-btn-ghost wc-btn-icon"
                  style={{ color: '#fca5a5', flexShrink: 0 }}
                  title="Session beenden"
                  aria-label="Session beenden"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}

            {sessions.length === 1 && (
              <p className="wc-muted" style={{ fontSize: 13, textAlign: 'center', padding: '8px 0', margin: 0 }}>
                Sie haben aktuell keine weiteren aktiven Sitzungen
              </p>
            )}
          </>
        )}
      </div>

      <div className="wc-alert wc-alert-info" style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
        <div>
          <strong>Sicherheitshinweis</strong>
          <p style={{ margin: '4px 0 0' }}>
            Wenn Sie eine Sitzung nicht erkennen, beenden Sie diese sofort und ändern Sie Ihr Passwort.
          </p>
        </div>
      </div>
    </div>
  )
}
